/**
 * sl-ya4v.7 + sl-hle4.8: Custom Domain & Public URL section
 *
 * Section component for the Custom Apps tab. Owns the three layers
 * of a workspace's public host:
 *   - Default host (`<shortId>.streamlain.site`) — read-only, always shown.
 *   - Branded subdomain (`<branded>.streamlain.site`) — Pro+, one per workspace.
 *   - Custom domain (`acme.example.com`) — Enterprise+, CNAME-verified.
 *
 * Effective-host precedence mirrors the backend (`custom > branded > default`).
 * Plan-gate hints surface for plans below the per-section feature key, with a
 * direct link to the Plans tab. Member-role users see the panel read-only.
 *
 * SOLID:
 *   - S: Public-host CRUD only — no broader workspace settings concerns.
 *   - D: Depends on GraphQLService + WorkspaceService + GuardService abstractions.
 */
import {
  Button,
  Input,
  Menu,
  MenuItem,
  notify,
  Skeleton,
  Switch,
} from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceShareSettingService } from '@affine/core/modules/share-setting';
import { useBrandedSubdomainAvailability } from '@affine/core/modules/streamlain/hooks/use-branded-subdomain-availability';
import {
  FEATURE_KEY_CUSTOM_APPS_BRANDED_SUBDOMAIN,
  FEATURE_KEY_CUSTOM_APPS_CREATE,
  FEATURE_KEY_CUSTOM_APPS_CUSTOM_DOMAIN,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useRenameBrandedSubdomain } from '@affine/core/modules/streamlain/hooks/use-rename-branded-subdomain';
import {
  type CustomAppInfo,
  useWorkspaceCustomApps,
} from '@affine/core/modules/streamlain/hooks/use-workspace-custom-apps';
import { useWorkspacePublicHost } from '@affine/core/modules/streamlain/hooks/use-workspace-public-host';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  CustomDomainKind,
  type CustomDomainSslStatus,
  type CustomDomainStatus,
  type ListCustomDomainsQuery,
  listCustomDomainsQuery,
  registerCustomDomainMutation,
  registerSubdomainMutation,
  removeCustomDomainMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';

import type { SettingState } from '../../types';
import {
  SettingsConfirmButton,
  SettingsNotice,
} from '../shared/settings-content';
import { useSettingsEntityList } from '../shared/use-settings-entity-list';
import { RenameBrandedSubdomainModal } from './rename-branded-subdomain-modal';
import * as styles from './styles.css';

// sl-ya4v.15: Apex is derived from the host snapshot so dev (`*.lvh.me:8080`)
// and prod (`*.streamlain.site`) read from the same source. The literal
// fallback only applies before the snapshot has loaded — at which point the
// rename CTA is hidden anyway.
const FALLBACK_APEX = 'streamlain.site';
function deriveApex(defaultHost: string | null): string {
  if (!defaultHost) return FALLBACK_APEX;
  const idx = defaultHost.indexOf('.');
  return idx >= 0 ? defaultHost.slice(idx + 1) : FALLBACK_APEX;
}

// Status enums come from backend GQL via codegen — schema.gql defines
// CustomDomainStatus + CustomDomainSslStatus + CustomDomainKind. A backend
// state added without touching the maps here is a compile error.
type AnyDomainState = CustomDomainStatus | CustomDomainSslStatus;

type CustomDomainEntry = ListCustomDomainsQuery['customDomains'][number];

const STATUS_VARIANT: Record<AnyDomainState, string> = {
  active: styles.statusActive,
  pending: styles.statusPending,
  verifying: styles.statusPending,
  provisioning: styles.statusPending,
  failed: styles.statusFailed,
};

const StatusBadge: FC<{ label: string; status: AnyDomainState }> = ({
  label,
  status,
}) => (
  <span className={clsx(styles.statusBadge, STATUS_VARIANT[status])}>
    {label}: {status}
  </span>
);

// sl-ya4v.4b-E: View-picker dropdown reused by both register-forms. Workspace-
// scoped — the feature-gate guarantees `custom_apps.create` so an empty
// list is a stub state, not an error.
const ViewPicker: FC<{
  views: CustomAppInfo[] | undefined;
  isLoading: boolean;
  value: string;
  onChange: (id: string) => void;
  placeholder: string;
  emptyHint: string;
  testId: string;
  disabled?: boolean;
}> = ({
  views,
  isLoading,
  value,
  onChange,
  placeholder,
  emptyHint,
  testId,
  disabled,
}) => {
  if (!isLoading && (!views || views.length === 0)) {
    return <div className={styles.sectionDescription}>{emptyHint}</div>;
  }
  const selected = views?.find(v => v.id === value);
  const selectedLabel = selected
    ? selected.title || selected.slug || selected.id
    : placeholder;
  return (
    <Menu
      items={
        views?.map(v => (
          <MenuItem
            key={v.id}
            onClick={() => onChange(v.id)}
            selected={v.id === value}
          >
            {v.title || v.slug || v.id}
          </MenuItem>
        )) ?? []
      }
    >
      <Button
        disabled={disabled || isLoading || !views}
        data-testid={testId}
        style={{ maxWidth: '100%' }}
      >
        {selectedLabel}
      </Button>
    </Menu>
  );
};

const PlanGateNotice: FC<{
  title: string;
  body: string;
  upgradeLabel: string;
  memberHint: string;
  isAdmin: boolean;
  onUpgrade: () => void;
}> = ({ title, body, upgradeLabel, memberHint, isAdmin, onUpgrade }) => (
  <SettingsNotice
    title={title}
    actions={
      isAdmin ? (
        <Button variant="primary" onClick={onUpgrade}>
          {upgradeLabel}
        </Button>
      ) : null
    }
  >
    {isAdmin ? body : memberHint}
  </SettingsNotice>
);

export const CustomDomainSection: FC<{
  onChangeSettingState?: (state: SettingState) => void;
}> = ({ onChangeSettingState }) => {
  const t = useI18n();
  const gqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const information = useWorkspaceInfo(workspaceService.workspace);
  const isAdmin = !!(information?.isOwner || information?.isAdmin);

  const { isFeatureAllowed } = useFeatureGate();
  const hasCustomApps = isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);
  const hasBranded = isFeatureAllowed(
    FEATURE_KEY_CUSTOM_APPS_BRANDED_SUBDOMAIN
  );
  const hasCustom = isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CUSTOM_DOMAIN);

  const { mutate: swrMutate } = useSWRConfig();
  const invalidateHostSnapshot = useCallback(
    () => swrMutate(['workspacePublicHost', workspaceId]),
    [swrMutate, workspaceId]
  );

  // sl-xm1c.2.4: Workspace-Admin Toggle for the Custom Apps feature.
  // Source-of-truth lives in WorkspaceShareSetting (existing entity for the
  // workspace-config GraphQL query). The toggle row is rendered admin-only;
  // members see only the read-only host panels below.
  const shareSetting = useService(WorkspaceShareSettingService).sharePreview;
  const customAppsEnabled = useLiveData(shareSetting.customAppsEnabled$);
  const shareSettingLoading = useLiveData(shareSetting.isLoading$);
  const handleToggleCustomApps = useAsyncCallback(
    async (checked: boolean) => {
      try {
        await shareSetting.setCustomAppsEnabled(checked);
      } catch (err) {
        notify.error({ error: UserFriendlyError.fromAny(err) });
      }
    },
    [shareSetting]
  );

  const {
    defaultHost,
    brandedHost,
    effectiveHost,
    isLoading: hostLoading,
    error: hostError,
  } = useWorkspacePublicHost(workspaceId);
  const apex = useMemo(() => deriveApex(defaultHost), [defaultHost]);

  // sl-ya4v.15 + sl-xm1c.3.1: rename + release sit on the dedicated
  // branded-subdomain mutations (not the generic remove) so the backend
  // enforces the 30-day reuse cooldown + 90-day 301 redirect lifetime and
  // writes the slug_history row inside the same transaction.
  const {
    rename: renameBranded,
    release: releaseBranded,
    isPending: renamePending,
  } = useRenameBrandedSubdomain(workspaceId);
  const {
    canRename,
    nextRenameAt,
    refresh: refreshAvailability,
  } = useBrandedSubdomainAvailability(workspaceId);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const formattedNextRenameAt = useMemo(() => {
    if (!nextRenameAt) return null;
    const date = new Date(nextRenameAt);
    if (Number.isNaN(date.valueOf())) return null;
    return date.toLocaleDateString();
  }, [nextRenameAt]);

  // sl-ya4v.4b-E: Custom-domain → custom-app binding picker. The list is
  // workspace-scoped (membership-gated by the resolver), so any admin who
  // can manage domains can also see all views to choose from.
  const { views: customApps, isLoading: viewsLoading } =
    useWorkspaceCustomApps(workspaceId);
  const viewById = useMemo(() => {
    const map = new Map<string, CustomAppInfo>();
    customApps?.forEach(v => map.set(v.id, v));
    return map;
  }, [customApps]);
  const renderViewLabel = useCallback(
    (id: string): string => {
      const v = viewById.get(id);
      return v ? v.title || v.slug : id;
    },
    [viewById]
  );

  const {
    data: domains,
    isLoading: domainsLoading,
    error: domainsError,
    refresh: refreshDomains,
  } = useSettingsEntityList<CustomDomainEntry>(async () => {
    const result = await gqlService.gql({
      query: listCustomDomainsQuery,
      variables: { workspaceId },
    });
    return result.customDomains;
  }, [gqlService, workspaceId]);

  const subdomainEntry =
    domains?.find(d => d.type === CustomDomainKind.subdomain) ?? null;
  const customEntries =
    domains?.filter(d => d.type === CustomDomainKind.custom) ?? [];

  const [subdomainInput, setSubdomainInput] = useState('');
  const [subdomainViewId, setSubdomainViewId] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [customViewId, setCustomViewId] = useState('');
  const [savingSubdomain, setSavingSubdomain] = useState(false);
  const [savingCustom, setSavingCustom] = useState(false);

  const goToPlans = useCallback(() => {
    onChangeSettingState?.({ activeTab: 'workspace:plans' });
  }, [onChangeSettingState]);

  const handleRegisterSubdomain = useAsyncCallback(async () => {
    const subdomain = subdomainInput.trim().toLowerCase();
    if (!subdomain || !subdomainViewId) return;
    setSavingSubdomain(true);
    try {
      await gqlService.gql({
        query: registerSubdomainMutation,
        variables: {
          workspaceId,
          subdomain,
          customAppId: subdomainViewId,
        },
      });
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.customApps.subdomain.savedToast'
          ](),
      });
      setSubdomainInput('');
      setSubdomainViewId('');
      await refreshDomains();
      await invalidateHostSnapshot();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setSavingSubdomain(false);
    }
  }, [
    gqlService,
    workspaceId,
    subdomainInput,
    subdomainViewId,
    t,
    refreshDomains,
    invalidateHostSnapshot,
  ]);

  const handleRegisterCustom = useAsyncCallback(async () => {
    const domain = customInput.trim().toLowerCase();
    if (!domain || !customViewId) return;
    setSavingCustom(true);
    try {
      await gqlService.gql({
        query: registerCustomDomainMutation,
        variables: {
          workspaceId,
          domain,
          customAppId: customViewId,
        },
      });
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.customApps.customDomain.savedToast'
          ](),
      });
      setCustomInput('');
      setCustomViewId('');
      await refreshDomains();
      await invalidateHostSnapshot();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setSavingCustom(false);
    }
  }, [
    gqlService,
    workspaceId,
    customInput,
    customViewId,
    t,
    refreshDomains,
    invalidateHostSnapshot,
  ]);

  const handleRemove = useCallback(
    async (domainId: string) => {
      try {
        await gqlService.gql({
          query: removeCustomDomainMutation,
          variables: { workspaceId, domainId },
        });
        notify.success({
          title:
            t['com.streamlain.settings.workspace.customApps.removedToast'](),
        });
        await refreshDomains();
        await invalidateHostSnapshot();
      } catch (err) {
        notify.error({ error: UserFriendlyError.fromAny(err) });
      }
    },
    [gqlService, workspaceId, t, refreshDomains, invalidateHostSnapshot]
  );

  // sl-ya4v.15 + sl-xm1c.3.1: Releasing a branded subdomain hits the dedicated
  // mutation — it deletes the row, writes the slug_history release marker
  // (30d reuse cooldown + 90d 301 redirect lifetime), and emits the audit
  // event in a single transaction. Generic `removeCustomDomain` would only
  // delete the row.
  const handleReleaseBranded = useCallback(async () => {
    try {
      await releaseBranded();
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.customApps.brandedSubdomain.releasedToast'
          ](),
      });
      await refreshDomains();
      await refreshAvailability();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    }
  }, [releaseBranded, refreshDomains, refreshAvailability, t]);

  const handleSubmitRename = useCallback(
    async (newLabel: string) => {
      try {
        await renameBranded(newLabel);
        notify.success({
          title:
            t[
              'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.savedToast'
            ](),
        });
        setRenameModalOpen(false);
        await refreshDomains();
        await refreshAvailability();
      } catch (err) {
        notify.error({ error: UserFriendlyError.fromAny(err) });
      }
    },
    [renameBranded, refreshDomains, refreshAvailability, t]
  );

  // Defense-in-depth: section is only rendered when the parent tab has
  // already gated on `FEATURE_KEY_CUSTOM_APPS_CREATE`. Direct-URL
  // navigation could still hit this code-path on a Free plan.
  if (!hasCustomApps) {
    return (
      <PlanGateNotice
        title={t[
          'com.streamlain.settings.workspace.customApps.planGate.customApps.title'
        ]()}
        body={t[
          'com.streamlain.settings.workspace.customApps.planGate.customApps.body'
        ]()}
        upgradeLabel={t[
          'com.streamlain.settings.workspace.customApps.planGate.upgradeAction'
        ]()}
        memberHint={t[
          'com.streamlain.settings.workspace.customApps.planGate.memberHint'
        ]()}
        isAdmin={isAdmin}
        onUpgrade={goToPlans}
      />
    );
  }

  if (hostLoading && defaultHost === null && !hostError) {
    return <Skeleton />;
  }

  return (
    <>
      {isAdmin ? (
        <SettingWrapper
          title={t[
            'com.streamlain.settings.workspace.customApps.adminToggle.title'
          ]()}
        >
          <SettingRow
            name={
              customAppsEnabled === false
                ? t[
                    'com.streamlain.settings.workspace.customApps.adminToggle.statusOff'
                  ]()
                : t[
                    'com.streamlain.settings.workspace.customApps.adminToggle.statusOn'
                  ]()
            }
            desc={t[
              'com.streamlain.settings.workspace.customApps.adminToggle.description'
            ]()}
          >
            <Switch
              checked={customAppsEnabled ?? true}
              onChange={handleToggleCustomApps}
              disabled={shareSettingLoading || customAppsEnabled === null}
              data-testid="custom-apps-admin-toggle"
            />
          </SettingRow>
          {customAppsEnabled === false ? (
            <div className={styles.sectionDescription}>
              {t[
                'com.streamlain.settings.workspace.customApps.adminToggle.disabledHint'
              ]()}
            </div>
          ) : null}
        </SettingWrapper>
      ) : null}
      <SettingWrapper
        title={t[
          'com.streamlain.settings.workspace.customApps.defaultHost.title'
        ]()}
      >
        <div className={styles.sectionDescription}>
          {t[
            'com.streamlain.settings.workspace.customApps.defaultHost.description'
          ]({ apex })}
        </div>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.defaultHost.label'
          ]()}
        >
          <span className={styles.hostValue}>{defaultHost ?? '—'}</span>
        </SettingRow>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.effectiveHost.label'
          ]()}
        >
          <span className={styles.hostValue}>
            {effectiveHost ?? defaultHost ?? '—'}
          </span>
        </SettingRow>
      </SettingWrapper>

      <SettingWrapper
        title={t[
          'com.streamlain.settings.workspace.customApps.brandedSubdomain.title'
        ]()}
      >
        <div className={styles.sectionDescription}>
          {t[
            'com.streamlain.settings.workspace.customApps.brandedSubdomain.description'
          ]({ apex })}
        </div>
        {!hasBranded ? (
          <PlanGateNotice
            title={t[
              'com.streamlain.settings.workspace.customApps.planGate.branded.title'
            ]()}
            body={t[
              'com.streamlain.settings.workspace.customApps.planGate.branded.body'
            ]({ apex })}
            upgradeLabel={t[
              'com.streamlain.settings.workspace.customApps.planGate.upgradeAction'
            ]()}
            memberHint={t[
              'com.streamlain.settings.workspace.customApps.planGate.memberHint'
            ]()}
            isAdmin={isAdmin}
            onUpgrade={goToPlans}
          />
        ) : domainsLoading && domains === undefined ? (
          <Skeleton />
        ) : domainsError ? (
          <div className={styles.sectionDescription}>{domainsError}</div>
        ) : subdomainEntry ? (
          <>
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.brandedSubdomain.label'
              ]()}
            >
              {/* sl-ya4v.8 F3: prefer the dev-rewritten brandedHost from the
               * host snapshot. Falling back to the raw DB value would show
               * `acme.streamlain.site` in dev while the actual reachable
               * host is `acme.lvh.me:8080`. */}
              <span className={styles.hostValue}>
                {brandedHost ?? subdomainEntry.domain}
              </span>
            </SettingRow>
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.statusLabel'
              ]()}
            >
              <StatusBadge
                label={t[
                  'com.streamlain.settings.workspace.customApps.dnsStatusLabel'
                ]()}
                status={subdomainEntry.status}
              />
            </SettingRow>
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.routesTo.label'
              ]()}
            >
              <span>{renderViewLabel(subdomainEntry.customAppId)}</span>
            </SettingRow>
            {isAdmin ? (
              <>
                <div className={styles.formRow}>
                  <Button
                    variant="primary"
                    onClick={() => setRenameModalOpen(true)}
                    disabled={!canRename || renamePending}
                    data-testid="custom-apps-subdomain-rename"
                  >
                    {t[
                      'com.streamlain.settings.workspace.customApps.brandedSubdomain.renameAction'
                    ]()}
                  </Button>
                  <SettingsConfirmButton
                    label={t[
                      'com.streamlain.settings.workspace.customApps.brandedSubdomain.releaseAction'
                    ]()}
                    confirmLabel={t[
                      'com.streamlain.settings.workspace.customApps.brandedSubdomain.releaseConfirm'
                    ]()}
                    cancelLabel={t['Cancel']()}
                    onConfirm={() => void handleReleaseBranded()}
                    variant="error"
                    dataTestId="custom-apps-subdomain-release"
                  />
                </div>
                {!canRename && formattedNextRenameAt ? (
                  <div className={styles.renameAvailability}>
                    {t[
                      'com.streamlain.settings.workspace.customApps.brandedSubdomain.rateLimitedHint'
                    ]({ date: formattedNextRenameAt })}
                  </div>
                ) : null}
                <RenameBrandedSubdomainModal
                  open={renameModalOpen}
                  onOpenChange={setRenameModalOpen}
                  currentLabel={subdomainEntry.domain.split('.')[0] ?? ''}
                  apex={apex}
                  saving={renamePending}
                  onSubmit={handleSubmitRename}
                  testId="custom-apps-subdomain-rename-modal"
                  strings={{
                    title:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.title'
                      ](),
                    fromLabel:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.fromLabel'
                      ](),
                    toLabel:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.toLabel'
                      ](),
                    inputPlaceholder:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.placeholder'
                      ](),
                    formatHint:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.formatHint'
                      ](),
                    formatError:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.formatError'
                      ](),
                    identicalError:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.identicalError'
                      ](),
                    cooldownNotice:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.cooldownNotice'
                      ](),
                    rateLimitNotice:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.rateLimitNotice'
                      ](),
                    saveLabel:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.confirm'
                      ](),
                    savingLabel:
                      t[
                        'com.streamlain.settings.workspace.customApps.brandedSubdomain.rename.confirming'
                      ](),
                    cancelLabel: t['Cancel'](),
                  }}
                />
              </>
            ) : null}
          </>
        ) : isAdmin ? (
          <>
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.routesTo.label'
              ]()}
            >
              <ViewPicker
                views={customApps}
                isLoading={viewsLoading}
                value={subdomainViewId}
                onChange={setSubdomainViewId}
                placeholder={t[
                  'com.streamlain.settings.workspace.customApps.viewPicker.placeholder'
                ]()}
                emptyHint={t[
                  'com.streamlain.settings.workspace.customApps.viewPicker.empty'
                ]()}
                testId="custom-apps-subdomain-view-picker"
              />
            </SettingRow>
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.brandedSubdomain.label'
              ]()}
            >
              <div className={styles.formInline}>
                <Input
                  value={subdomainInput}
                  onChange={setSubdomainInput}
                  placeholder={t[
                    'com.streamlain.settings.workspace.customApps.brandedSubdomain.placeholder'
                  ]()}
                  endFix={<span className={styles.inputSuffix}>.{apex}</span>}
                  data-testid="custom-apps-subdomain-input"
                  style={{ minWidth: 220 }}
                />
                <Button
                  variant="primary"
                  onClick={handleRegisterSubdomain}
                  disabled={
                    savingSubdomain ||
                    subdomainInput.trim().length === 0 ||
                    !subdomainViewId
                  }
                  data-testid="custom-apps-subdomain-save"
                >
                  {savingSubdomain
                    ? t[
                        'com.streamlain.settings.workspace.customApps.savingAction'
                      ]()
                    : t[
                        'com.streamlain.settings.workspace.customApps.saveAction'
                      ]()}
                </Button>
              </div>
            </SettingRow>
          </>
        ) : (
          <div className={styles.sectionDescription}>
            {t[
              'com.streamlain.settings.workspace.customApps.brandedSubdomain.memberHint'
            ]()}
          </div>
        )}
      </SettingWrapper>

      <SettingWrapper
        title={t[
          'com.streamlain.settings.workspace.customApps.customDomain.title'
        ]()}
      >
        <div className={styles.sectionDescription}>
          {t[
            'com.streamlain.settings.workspace.customApps.customDomain.description'
          ]()}
        </div>
        {!hasCustom ? (
          <PlanGateNotice
            title={t[
              'com.streamlain.settings.workspace.customApps.planGate.custom.title'
            ]()}
            body={t[
              'com.streamlain.settings.workspace.customApps.planGate.custom.body'
            ]()}
            upgradeLabel={t[
              'com.streamlain.settings.workspace.customApps.planGate.upgradeAction'
            ]()}
            memberHint={t[
              'com.streamlain.settings.workspace.customApps.planGate.memberHint'
            ]()}
            isAdmin={isAdmin}
            onUpgrade={goToPlans}
          />
        ) : domainsLoading && domains === undefined ? (
          <Skeleton />
        ) : domainsError ? (
          <div className={styles.sectionDescription}>{domainsError}</div>
        ) : (
          <>
            {customEntries.map(entry => (
              <div key={entry.id}>
                <SettingRow
                  name={t[
                    'com.streamlain.settings.workspace.customApps.customDomain.label'
                  ]()}
                >
                  <span className={styles.hostValue}>{entry.domain}</span>
                </SettingRow>
                <SettingRow
                  name={t[
                    'com.streamlain.settings.workspace.customApps.statusLabel'
                  ]()}
                >
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <StatusBadge
                      label={t[
                        'com.streamlain.settings.workspace.customApps.dnsStatusLabel'
                      ]()}
                      status={entry.status}
                    />
                    <StatusBadge
                      label={t[
                        'com.streamlain.settings.workspace.customApps.sslStatusLabel'
                      ]()}
                      status={entry.sslStatus}
                    />
                  </span>
                </SettingRow>
                <SettingRow
                  name={t[
                    'com.streamlain.settings.workspace.customApps.routesTo.label'
                  ]()}
                >
                  <span>{renderViewLabel(entry.customAppId)}</span>
                </SettingRow>
                {entry.verificationToken && entry.status !== 'active' ? (
                  <div className={styles.verificationBlock}>
                    <div className={styles.verificationLabel}>
                      {t[
                        'com.streamlain.settings.workspace.customApps.customDomain.verificationHint'
                      ]()}
                    </div>
                    <code>{entry.verificationToken}</code>
                  </div>
                ) : null}
                {isAdmin ? (
                  <div className={styles.formRow}>
                    <SettingsConfirmButton
                      label={t[
                        'com.streamlain.settings.workspace.customApps.customDomain.removeAction'
                      ]()}
                      confirmLabel={t[
                        'com.streamlain.settings.workspace.customApps.customDomain.removeConfirm'
                      ]()}
                      cancelLabel={t['Cancel']()}
                      onConfirm={() => void handleRemove(entry.id)}
                      variant="error"
                      dataTestId={`custom-apps-custom-remove-${entry.id}`}
                    />
                  </div>
                ) : null}
              </div>
            ))}
            {isAdmin ? (
              <>
                <SettingRow
                  name={t[
                    'com.streamlain.settings.workspace.customApps.routesTo.label'
                  ]()}
                >
                  <ViewPicker
                    views={customApps}
                    isLoading={viewsLoading}
                    value={customViewId}
                    onChange={setCustomViewId}
                    placeholder={t[
                      'com.streamlain.settings.workspace.customApps.viewPicker.placeholder'
                    ]()}
                    emptyHint={t[
                      'com.streamlain.settings.workspace.customApps.viewPicker.empty'
                    ]()}
                    testId="custom-apps-custom-view-picker"
                  />
                </SettingRow>
                <SettingRow
                  name={t[
                    'com.streamlain.settings.workspace.customApps.customDomain.label'
                  ]()}
                >
                  <div className={styles.formInline}>
                    <Input
                      value={customInput}
                      onChange={setCustomInput}
                      placeholder={t[
                        'com.streamlain.settings.workspace.customApps.customDomain.placeholder'
                      ]()}
                      data-testid="custom-apps-custom-input"
                      style={{ minWidth: 240 }}
                    />
                    <Button
                      variant="primary"
                      onClick={handleRegisterCustom}
                      disabled={
                        savingCustom ||
                        customInput.trim().length === 0 ||
                        !customViewId
                      }
                      data-testid="custom-apps-custom-save"
                    >
                      {savingCustom
                        ? t[
                            'com.streamlain.settings.workspace.customApps.savingAction'
                          ]()
                        : t[
                            'com.streamlain.settings.workspace.customApps.addAction'
                          ]()}
                    </Button>
                  </div>
                </SettingRow>
              </>
            ) : customEntries.length === 0 ? (
              <div className={styles.sectionDescription}>
                {t[
                  'com.streamlain.settings.workspace.customApps.customDomain.memberHint'
                ]()}
              </div>
            ) : null}
          </>
        )}
      </SettingWrapper>
    </>
  );
};
